import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
// Import heroicons pour les icônes
import {
  StarIcon,
  CubeIcon,
  TruckIcon,
  CameraIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

/**
 * Page publique de feedback/avis après commande
 * Accessible via /feedback/:orderId sans authentification
 */
const PublicFeedbackView = () => {
  const { orderId } = useParams();

  // États pour les données
  const [orderInfo, setOrderInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackExists, setFeedbackExists] = useState(false);

  // États pour le formulaire
  const [productRating, setProductRating] = useState(0);
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Constantes
  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const MAX_COMMENT_LENGTH = 1000;

  // Chargement des données de la commande
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Vérifier d'abord si un feedback existe déjà
        const checkResponse = await api.get(`/order-feedback/public/${orderId}/check`);
        if (checkResponse.data.exists) {
          setFeedbackExists(true);
          setLoading(false);
          return;
        }

        // Récupérer les informations de la commande
        const orderResponse = await api.get(`/order-feedback/public/${orderId}`);
        const data = orderResponse.data.data;

        setOrderInfo(data);

        // Pré-remplir les informations client si disponibles
        if (data.customer) {
          setCustomerName(data.customer.name || '');
          setCustomerPhone(data.customer.phone || '');
        }
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        setError(err.response?.data?.error || 'Impossible de charger les informations de la commande');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderData();
    }
  }, [orderId]);

  // Gestion de la sélection d'images
  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - images.length;
    const filesToAdd = files.slice(0, remainingSlots);

    // Valider et filtrer les fichiers
    const validFiles = filesToAdd.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`Le fichier ${file.name} dépasse la taille maximale de 5MB`);
        return false;
      }
      if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
        alert(`Le fichier ${file.name} n'est pas un format d'image valide`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Ajouter les fichiers à l'état
    setImages(prev => [...prev, ...validFiles]);

    // Créer les aperçus
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, {
          file,
          preview: reader.result
        }]);
      };
      reader.readAsDataURL(file);
    });

    // Réinitialiser l'input
    e.target.value = '';
  }, [images.length]);

  // Suppression d'une image
  const handleRemoveImage = useCallback((index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (productRating === 0) {
      alert('Veuillez noter les articles commandés');
      return;
    }

    try {
      setSubmitting(true);

      // Préparer les données FormData pour l'upload
      const formData = new FormData();
      formData.append('productRating', productRating);
      if (deliveryRating > 0) {
        formData.append('deliveryRating', deliveryRating);
      }
      if (comment.trim()) {
        formData.append('comment', comment.trim());
      }
      formData.append('customerName', customerName);
      formData.append('customerPhone', customerPhone);

      // Ajouter les images
      images.forEach(image => {
        formData.append('images', image);
      });

      // Envoyer la requête
      await api.post(`/order-feedback/public/${orderId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Erreur lors de la soumission:', err);
      alert(err.response?.data?.error || 'Erreur lors de l\'envoi de votre avis');
    } finally {
      setSubmitting(false);
    }
  };

  // Composant pour les étoiles de notation
  const StarIconRating = ({ rating, setRating, label, icon: Icon }) => (
    <div className="mb-6">
      <label className="flex items-center gap-2 text-gray-700 font-medium mb-3">
        <Icon className="h-5 w-5 text-blue-600" />
        {label}
      </label>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <StarIcon
              className={`h-10 w-10 ${
                star <= rating
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300 hover:text-yellow-200'
              }`}
            />
          </button>
        ))}
      </div>
      <p className="text-sm text-gray-500 mt-1">
        {rating === 0 && 'Cliquez pour noter'}
        {rating === 1 && 'Très insatisfait'}
        {rating === 2 && 'Insatisfait'}
        {rating === 3 && 'Correct'}
        {rating === 4 && 'Satisfait'}
        {rating === 5 && 'Très satisfait'}
      </p>
    </div>
  );

  // Affichage du chargement
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Affichage si feedback déjà soumis
  if (feedbackExists) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Avis déjà enregistré</h2>
          <p className="text-gray-600">
            Vous avez déjà donné votre avis pour cette commande. Merci pour votre retour !
          </p>
        </div>
      </div>
    );
  }

  // Affichage de l'erreur
  if (error || !orderInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Erreur</h2>
          <p className="text-gray-600">{error || "Commande introuvable"}</p>
        </div>
      </div>
    );
  }

  // Affichage du succès après soumission
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <CheckCircleIcon className="h-12 w-12 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Merci pour votre avis !</h2>
          <p className="text-gray-600 mb-6">
            Votre retour nous aide à améliorer nos services et notre qualité.
          </p>
          <div className="flex justify-center gap-2 mb-4">
            {[...Array(productRating)].map((_, i) => (
              <StarIcon key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <p className="text-sm text-gray-500">
            Commande #{orderInfo.orderNumber}
          </p>
        </div>
      </div>
    );
  }

  // Formulaire de feedback
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* En-tête */}
        <div className="bg-white rounded-t-2xl shadow-sm p-6 border-b border-gray-100">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Donnez votre avis
          </h1>
          <p className="text-gray-600">
            Commande #{orderInfo.orderNumber} du {new Date(orderInfo.createdAt).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Résumé des articles */}
        <div className="bg-white p-6 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CubeIcon className="h-5 w-5 text-blue-600" />
            Articles commandés
          </h3>
          <div className="space-y-3">
            {orderInfo.items.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    className="h-12 w-12 rounded-lg object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <CubeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{item.name}</p>
                  {item.variantTitle && (
                    <p className="text-sm text-gray-500">{item.variantTitle}</p>
                  )}
                </div>
                <span className="text-sm text-gray-500">x{item.quantity}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="bg-white rounded-b-2xl shadow-sm p-6">
          {/* Note des produits (obligatoire) */}
          <StarIconRating
            rating={productRating}
            setRating={setProductRating}
            label="Comment notez-vous les articles ? *"
            icon={CubeIcon}
          />

          {/* Note de la livraison (optionnelle si livreur) */}
          {orderInfo.deliveryPerson && (
            <StarIconRating
              rating={deliveryRating}
              setRating={setDeliveryRating}
              label={`Comment notez-vous la livraison ${orderInfo.deliveryPerson.name ? `par ${orderInfo.deliveryPerson.name}` : ''} ?`}
              icon={TruckIcon}
            />
          )}

          {/* Commentaire */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              Votre commentaire (optionnel)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
              placeholder="Partagez votre expérience avec nous..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={4}
            />
            <p className="text-sm text-gray-500 mt-1 text-right">
              {comment.length}/{MAX_COMMENT_LENGTH}
            </p>
          </div>

          {/* Upload d'images */}
          <div className="mb-6">
            <label className="block text-gray-700 font-medium mb-2">
              <CameraIcon className="h-5 w-5 inline-block mr-2 text-blue-600" />
              Ajouter des photos (optionnel)
            </label>

            {/* Aperçus des images */}
            {imagePreviews.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                {imagePreviews.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img.preview}
                      alt={`Preview ${index + 1}`}
                      className="h-20 w-20 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Zone d'upload */}
            {images.length < MAX_IMAGES && (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <ArrowUpTrayIcon className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  Cliquez pour ajouter des photos
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  {images.length}/{MAX_IMAGES} photos - Max 5MB par image
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Informations du client */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Votre nom
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Votre nom"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Votre téléphone
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Votre numéro"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Bouton de soumission */}
          <button
            type="submit"
            disabled={submitting || productRating === 0}
            className={`w-full py-4 rounded-lg text-white font-semibold text-lg transition-all ${
              submitting || productRating === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
                Envoi en cours...
              </span>
            ) : (
              'Envoyer mon avis'
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Votre avis nous aide à améliorer notre service. Merci !
          </p>
        </form>
      </div>
    </div>
  );
};

export default PublicFeedbackView;
